// src/hooks/useAutoRefresh.js
// Calls the provided `refreshFn` immediately on mount, then every `intervalMs`.
// Returns `lastUpdated` — a Date object updated after each successful call.
// The interval is cleared automatically when the component unmounts.

import { useState, useEffect, useRef } from 'react';

export function useAutoRefresh(refreshFn, intervalMs = 30000) {
  const [lastUpdated, setLastUpdated] = useState(null);
  const fnRef = useRef(refreshFn);

  // Keep the ref current so we always call the latest version of the function
  useEffect(() => { fnRef.current = refreshFn; }, [refreshFn]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      await fnRef.current();
      if (!cancelled) setLastUpdated(new Date());
    }

    run(); // immediate first call
    const id = setInterval(run, intervalMs);
    return () => { cancelled = true; clearInterval(id); };
  }, [intervalMs]); // eslint-disable-line

  return lastUpdated;
}
